import { logger } from "@/lib/utils/logger";
import type { DataCallMetrics, SLOKey } from "./types";

export function calculatePercentiles(metrics: DataCallMetrics[]): {
  p50: number;
  p95: number;
  p99: number;
} {
  if (metrics.length === 0) {
    return { p50: 0, p95: 0, p99: 0 };
  }

  const latencies = metrics.map((m) => m.latency).sort((a, b) => a - b);
  const percentile = (p: number) =>
    latencies[Math.floor(latencies.length * p)] ?? 0;

  return {
    p50: percentile(0.5),
    p95: percentile(0.95),
    p99: percentile(0.99),
  };
}

export function calculateErrorRate(metrics: DataCallMetrics[]): number {
  if (metrics.length === 0) return 0;
  const errors = metrics.filter((m) => m.errorRate > 0).length;
  return errors / metrics.length;
}

export function determineSloKey(metric: DataCallMetrics): SLOKey {
  if (metric.databaseQuery) {
    return "DATABASE_QUERY";
  }
  if (
    metric.operation?.includes("cache") &&
    !metric.operation?.includes("api") &&
    !metric.operation?.startsWith("api.")
  ) {
    return "CACHE_OPERATION";
  }
  if (metric.operation?.includes("edge")) {
    return "EDGE_FUNCTION";
  }
  return "API_ROUTE";
}

export function filterMetricsForSLO(
  metrics: DataCallMetrics[],
  sloKey: string,
): DataCallMetrics[] {
  let filtered = metrics;

  if (sloKey === "DATABASE_QUERY") {
    filtered = filtered.filter((m) => m.databaseQuery === true);
  } else if (sloKey === "CACHE_OPERATION") {
    filtered = filtered.filter(
      (m) =>
        m.operation?.includes("cache") &&
        !m.operation?.includes("api") &&
        !m.operation?.startsWith("api."),
    );
  } else if (sloKey === "EDGE_FUNCTION") {
    filtered = filtered.filter((m) => m.operation?.includes("edge"));
  } else if (sloKey === "API_ROUTE") {
    filtered = filtered.filter(
      (m) =>
        !m.databaseQuery &&
        (!m.operation?.includes("cache") ||
          m.operation?.includes("api") ||
          m.operation?.startsWith("api.")),
    );
  }

  const isDevelopment = process.env.NODE_ENV === "development";
  if (isDevelopment && filtered.length >= 10) {
    const latencies = filtered.map((m) => m.latency).sort((a, b) => a - b);
    const p99Baseline = latencies[Math.floor(latencies.length * 0.99)] ?? 0;
    const coldStartThreshold = Math.max(p99Baseline * 1.5, 1000);

    const beforeFilter = filtered.length;
    filtered = filtered.filter((m) => m.latency < coldStartThreshold);

    if (beforeFilter !== filtered.length) {
      logger.debug("[SLO] Filtered cold start requests", {
        slo: sloKey,
        before: beforeFilter,
        after: filtered.length,
        threshold: coldStartThreshold,
      });
    }
  }

  return filtered;
}
